// utils/fieldFormatter.ts
import dayjs from 'dayjs';
/**
 * 格式化函数集合
 */
const formatFunctions = {
    toUpper: (val) => String(val).toUpperCase(),
    toLower: (val) => String(val).toLowerCase(),
    substr: (val, start, len) => String(val).substr(start, len),
    formatPhone: (val) => String(val).replace(/(\d{3})\d{4}(\d{4})/, '$1****$2'),
    formatDate: (val, fmt) => dayjs(val).format(fmt),
    /**
     * 💡 新增：lastday 判断当前时间离最近几个月，几天，几小时，几分值的处理
     */
    lastday: (val) => {
        if (!val)
            return '-';
        const now = dayjs();
        const target = dayjs(val);
        if (!target.isValid())
            return String(val);
        const diffMinutes = now.diff(target, 'minute');
        const diffHours = now.diff(target, 'hour');
        const diffDays = now.diff(target, 'day');
        const diffMonths = now.diff(target, 'month');
        const diffYears = now.diff(target, 'year');
        if (diffMinutes < 1)
            return '刚刚';
        if (diffMinutes < 60)
            return `${diffMinutes} 分钟前`;
        if (diffHours < 24)
            return `${diffHours} 小时前`;
        if (diffDays < 30)
            return `${diffDays} 天前`;
        if (diffMonths < 12)
            return `${diffMonths} 个月前`;
        return `${diffYears} 年前`;
    },
    /**
     * 值映射（支持多值）
     */
    mapValue: (mapStr, val, separator = ',') => {
        try {
            if (val === undefined || val === null || val === '')
                return '-';
            const mapObj = JSON.parse(mapStr);
            const values = String(val)
                .split(separator)
                .map(v => v.trim())
                .filter(Boolean);
            const labels = values.map(v => mapObj[v] ?? v);
            return labels.join(' / ');
        }
        catch {
            return String(val ?? '-');
        }
    },
};
/**
 * 模板字符串格式化
 */
function formatTemplate(template, record) {
    return template.replace(/\$\{([^}]+)\}/g, (_, key) => {
        const value = record[key.trim()];
        return value !== undefined && value !== null ? String(value) : '';
    });
}
/**
 * 通用字段值格式化函数
 */
export function formatFieldValue(record, field) {
    const rawValue = record[field.fieldEn];
    const format = field.displayFormat?.trim();
    if (!format)
        return rawValue ?? '-';
    /**
     * 日期格式支持
     */
    if (format.includes('yyyy') || format.includes('MM')) {
        const dayjsFormat = format
            .replace(/yyyy/g, 'YYYY')
            .replace(/mm/g, 'MM')
            .replace(/dd/g, 'DD');
        return rawValue ? dayjs(rawValue).format(dayjsFormat) : '-';
    }
    /**
     * 模板拼接
     */
    if (format.includes('${') && !format.includes('(')) {
        return formatTemplate(format, record);
    }
    /**
     * 函数调用格式
     */
    try {
        return format.replace(/(\w+)\(([^)]*)\)/g, (_, funcName, funcArgs) => {
            const fn = formatFunctions[funcName];
            if (!fn)
                return '';
            /**
             * 💡 特殊处理 lastday: 支持 lastday() 自动传入 rawValue
             */
            if (funcName === 'lastday') {
                const arg = funcArgs.trim();
                if (!arg) {
                    return fn(rawValue);
                }
            }
            // 特殊处理 mapValue
            if (funcName === 'mapValue') {
                const rawArg = funcArgs.trim();
                const m = rawArg.match(/^['"]([\s\S]+?)['"]$/);
                const mapStr = m ? m[1] : rawArg;
                return fn(mapStr, rawValue);
            }
            // 普通函数参数解析
            const args = funcArgs
                .split(',')
                .map((arg) => {
                const a = arg.trim();
                // 支持 ${field}
                if (a.startsWith('${') && a.endsWith('}')) {
                    const key = a.slice(2, -1).trim();
                    return record[key];
                }
                return a.replace(/^['"]|['"]$/g, '');
            });
            return fn(...args);
        });
    }
    catch (err) {
        console.warn('formatFieldValue error:', err);
        return rawValue ?? '-';
    }
}
